/**
 * DownloadButton — botão reutilizável que dispara um GET autenticado num
 * endpoint que responde com um arquivo (XLSX, PDF, CSV…), grava o blob e
 * força o download no browser sem sair da página.
 *
 * Por que não usar um `<a href>` puro:
 *  - As rotas de relatório usam sessão de cookie (`credentials: 'include'`)
 *    e um `<a target=_blank>` no Chrome pode não enviar SameSite=strict —
 *    fetch com credenciais é mais previsível.
 *  - Precisamos tratar 401/403/400 com mensagem amigável, e um <a> não
 *    permite interceptar a resposta.
 *  - O feedback "Baixando…" evita clique duplo do usuário quando o servidor
 *    demora pra gerar o arquivo (financial XLSX de mês inteiro pode passar
 *    de 1s).
 *
 * O componente respeita `Content-Disposition: attachment; filename="..."`
 * do servidor. Se o header não vier, usa o `filename` prop como fallback.
 */
import { useState, type ReactNode } from 'react';
import { IconDownload } from './icons.js';

export type DownloadButtonVariant = 'primary' | 'secondary' | 'ghost';

export interface DownloadButtonProps {
  /** URL absoluta (deve começar com `/`) para o endpoint de download. */
  href: string;
  /** Sugestão de filename caso o servidor não envie Content-Disposition. */
  filename: string;
  /** Texto exibido no botão. Default: "Exportar". */
  label?: string;
  /** Estilo do botão. Default: 'primary'. */
  variant?: DownloadButtonVariant;
  /** Ícone à esquerda do label. Default: <IconDownload />. Passe `null` para nenhum. */
  icon?: ReactNode | null;
  /** Se true, aplica `.nx-btn--sm` (menor). */
  small?: boolean;
  /** Desabilita o botão externamente (ex.: filtros faltando). */
  disabled?: boolean;
  /**
   * Título/tooltip nativo do botão. Útil pra explicar por que está desabilitado
   * (ex.: "Selecione um período para exportar").
   */
  title?: string;
  /**
   * Handler opcional pra erros de rede/HTTP. Se não vier, exibe `alert()`.
   * Recebe status HTTP + código textual (ex.: "not_found", "invalid_month").
   */
  onError?: (status: number, code: string) => void;
  /** Handler opcional após sucesso — ex.: para exibir toast. */
  onSuccess?: () => void;
}

/**
 * Extrai o filename do header `Content-Disposition`, se presente. Aceita as
 * variações mais comuns: `attachment; filename="foo.xlsx"` ou sem aspas.
 */
function filenameFromDisposition(header: string | null): string | null {
  if (!header) return null;
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(header);
  return match?.[1] ?? null;
}

/**
 * Erro serializado do backend nxvolt: sempre `{ error: string }`. Se o body
 * não for JSON válido (ex.: HTML de 500), volta um code genérico.
 */
async function readErrorCode(res: Response): Promise<string> {
  try {
    const body = (await res.json()) as { error?: string };
    return body.error ?? `http_${res.status}`;
  } catch {
    return `http_${res.status}`;
  }
}

export function DownloadButton({
  href,
  filename,
  label = 'Exportar',
  variant = 'primary',
  icon,
  small = false,
  disabled = false,
  title,
  onError,
  onSuccess,
}: DownloadButtonProps) {
  const [busy, setBusy] = useState(false);

  async function handleClick() {
    if (busy || disabled) return;
    setBusy(true);
    try {
      const res = await fetch(href, {
        credentials: 'include',
        headers: { Accept: 'application/octet-stream, application/pdf, */*' },
      });

      if (!res.ok) {
        // 401 → volta pra tela de login (mesmo padrão do resto do app: o
        // AuthContext redireciona quando `me` volta 401, mas aqui um
        // fetch direto não passa por lá — cutucamos manualmente).
        if (res.status === 401) {
          window.location.href = '/login';
          return;
        }
        const code = await readErrorCode(res);
        if (onError) onError(res.status, code);
        else if (res.status === 403) alert('Você não tem permissão para exportar este relatório.');
        else if (res.status === 404) alert('Recurso não encontrado.');
        else alert(`Falha ao exportar (${res.status}): ${code}`);
        return;
      }

      const blob = await res.blob();
      const suggested =
        filenameFromDisposition(res.headers.get('Content-Disposition')) ?? filename;

      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = suggested;
      // Alguns browsers ignoram `download` quando o <a> não está no DOM.
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Timeout curto evita revogar antes do browser iniciar o save-as em
      // Firefox (a spec permite, mas na prática às vezes falha).
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      onSuccess?.();
    } catch (err) {
      const message = err instanceof Error ? err.message : 'erro de rede';
      if (onError) onError(0, message);
      else alert(`Falha ao exportar: ${message}`);
    } finally {
      setBusy(false);
    }
  }

  const classes = ['nx-btn'];
  if (variant === 'secondary') classes.push('nx-btn--ghost-dark');
  else if (variant === 'ghost') classes.push('nx-btn--ghost');
  if (small) classes.push('nx-btn--sm');

  // `icon={null}` → some com o ícone (para casos em que o rótulo já basta,
  // como um link inline "Recibo PDF" numa tabela).
  const renderedIcon =
    icon === null ? null : (icon ?? <IconDownload aria-hidden="true" />);

  return (
    <button
      type="button"
      className={classes.join(' ')}
      onClick={() => void handleClick()}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      title={title}
    >
      {renderedIcon}
      <span>{busy ? 'Baixando…' : label}</span>
    </button>
  );
}

export default DownloadButton;
